/* @flow */

import * as React from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import classNames from 'classnames';
import { css } from 'glamor';
import { get } from 'lodash/fp';
import styled from '@emotion/styled';
import { withTheme } from 'emotion-theming';
import { padding, position } from 'polished';

import Glyph from './Glyph';
import Tappable from './Tappable';

type Props = {
  animation?: string,
  bare?: boolean,
  children?: any,
  className?: string,
  fullScreen?: boolean,
  isOpen?: boolean,
  noPadding?: boolean,
  positionStyle?: Object,
  lightCloseButton?: boolean,
  onCancel?: () => void,
  shouldCloseOnOverlayClick?: boolean,
  showCloseButton?: boolean,
  size?: 'auto' | 'small' | 'medium' | 'large',
  theme: Object
};

const CloseButton = styled.span(
  {
    alignItems: 'center',
    background: 'none',
    border: 'none',
    display: 'flex',
    justifyContent: 'center',
    lineHeight: 1,
    outline: 'none',
    padding: 0,
    position: 'absolute',
    right: 0,
    textAlign: 'center',
    textDecoration: 'none',
    top: 0,
    width: 50,
    height: 50,
    '&:hover, &:focus': {
      opacity: 0.5,
      textDecoration: 'none'
    },
    '&:active': {
      opacity: 0.5,
      textDecoration: 'none',
      transform: 'scale(0.8)'
    }
  },
  ({ theme, lightCloseButton }) => ({
    color: lightCloseButton ? theme.color.white : theme.color.basis3,
    fontSize: theme.fontSize.xlarge
  })
);

const animations = {
  modalUp: 'modalUp 2s',
  modalEnter: 'modalEnter 0.2s cubic-bezier(1,.01,.1,1.32)'
};

function createOverlayClassName({ theme, noPadding }) {
  return css({
    backgroundColor: 'rgba(0, 0, 0, 0.68)',
    ...position('fixed', '0'),
    ...(noPadding ? {} : padding(theme.spacing.xsmall, theme.spacing.xsmall)),
    overflow: 'scroll',
    WebkitOverflowScrolling: 'touch',
    zIndex: theme.zIndex.modal,
    [theme.mediaQuery.desktopAndUp]: {
      ...padding(theme.spacing.large, theme.spacing.xsmall)
    }
  }).toString();
}

function createContentClassName({
  animation,
  bare,
  fullScreen,
  size = 'medium',
  theme,
  positionStyle = { position: 'relative' }
}) {
  return css({
    animation: get(animation, animations),
    backgroundColor: !bare ? 'white' : 'transparent',
    borderColor: '#cccccc',
    borderStyle: 'solid',
    borderWidth: !bare ? 1 : 0,
    color: theme.color.basis2,
    justifyContent: 'center',
    margin: '0 auto',
    top: '90px',
    maxWidth: '100%',
    outline: 'none',
    overflow: 'hidden',
    padding: !bare ? theme.spacing.small : 0,
    ...(fullScreen
      ? {
          ...position('absolute', 0)
        }
      : {
          ...positionStyle,
          width: {
            auto: 'auto',
            small: 480,
            medium: 760,
            large: 1140
          }[size]
        }),
    [theme.mediaQuery.desktopAndUp]: {
      boxShadow: 'rgba(0, 0, 0, 0.4) 0 0 50px 50px',
      borderRadius: theme.borderRadius.default,
      ...(!bare ? padding(theme.spacing.default, theme.spacing.xxlarge) : {})
    }
  }).toString();
}

class Modal extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    animation: 'modalEnter',
    bare: false,
    noPadding: false,
    positionStyle: undefined,
    className: undefined,
    children: undefined,
    fullScreen: false,
    isOpen: false,
    lightCloseButton: false,
    onCancel: undefined,
    shouldCloseOnOverlayClick: true,
    showCloseButton: true,
    size: 'medium'
  };

  handleKeyPress = event => {
    const { onCancel, isOpen } = this.props;

    if (isOpen && event.keyCode === 27 && onCancel) {
      // Flow$Ignore
      onCancel();
    }
  };

  render() {
    const {
      animation,
      bare,
      children,
      className,
      fullScreen,
      isOpen,
      lightCloseButton,
      positionStyle,
      onCancel,
      noPadding,
      shouldCloseOnOverlayClick,
      showCloseButton,
      size,
      theme
    } = this.props;
    const contentClassName = createContentClassName({
      animation,
      bare,
      fullScreen,
      size,
      theme,
      positionStyle
    });
    const overlayClassName = createOverlayClassName({ theme, noPadding });

    return (
      <ReactModal
        appElement={document.getElementById('fantasy')}
        className={classNames(contentClassName, className)}
        contentLabel="Modal"
        isOpen={isOpen}
        onAfterOpen={() => {
          const [overlay] = document.getElementsByClassName(overlayClassName);
          if (overlay) {
            overlay.scrollTop = 0;
          }
        }}
        onRequestClose={onCancel}
        overlayClassName={overlayClassName}
        shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      >
        {children}
        {showCloseButton && (
          <Tappable component={CloseButton} lightCloseButton={lightCloseButton} onTap={onCancel}>
            <Glyph icon="ios-close-empty" size="medium" />
          </Tappable>
        )}
      </ReactModal>
    );
  }
}

export default withTheme(Modal);
